import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight } from 'lucide-react';
import styles from './ReviewPanel.module.css';
import { charColor } from './charColor';

export function EditableList({
  items,
  maxChar,
  onUpdate,
  disabled = false,
}: {
  items: string[];
  maxChar: number;
  onUpdate: (updated: string[]) => void;
  /** Read-only (e.g. launched campaigns, which can't be edited in place). */
  disabled?: boolean;
}) {
  const [local, setLocal] = useState(items);
  const editingRef = useRef(false);

  useEffect(() => {
    // Only sync from parent when not actively editing
    if (!editingRef.current) setLocal(items);
  }, [items]);

  const update = (i: number, val: string) => {
    const next = [...local];
    next[i] = val;
    setLocal(next);
  };

  return (
    <div className={styles.editableList}>
      {local.map((item, i) => (
        <div key={i} className={styles.editableRow}>
          <input
            className={`${styles.editableInput} ${item.length > maxChar ? styles.inputOver : ''}`}
            value={item}
            onChange={(e) => update(i, e.target.value)}
            onFocus={() => {
              editingRef.current = true;
            }}
            onBlur={() => {
              editingRef.current = false;
              if (!disabled) onUpdate(local);
            }}
            maxLength={maxChar + 5}
            readOnly={disabled}
          />
          <span className={styles.charCount} style={{ color: charColor(item.length, maxChar) }}>
            {item.length}/{maxChar}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Click-to-edit monthly budget for a deployment. Stored on platformData as
 * `dailyBudget`; we present it as $/mo and convert on commit. `helperText`
 * lets callers show context like "shared across 3 ads".
 */
export function MonthlyBudgetField({
  dailyBudget,
  onSave,
  disabled = false,
  helperText,
}: {
  dailyBudget: number;
  onSave: (nextDailyBudget: number) => void;
  disabled?: boolean;
  helperText?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(Math.round(dailyBudget * 30)));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(String(Math.round(dailyBudget * 30)));
  }, [dailyBudget, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    const monthly = Number(draft.replace(/[^0-9.]/g, ''));
    setEditing(false);
    if (Number.isFinite(monthly) && monthly > 0) {
      const nextDaily = Math.round((monthly / 30) * 100) / 100;
      if (nextDaily !== dailyBudget) onSave(nextDaily);
    }
  };

  return (
    <div className={styles.metaStat}>
      {editing ? (
        <input
          ref={inputRef}
          className={styles.budgetInput}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setDraft(String(Math.round(dailyBudget * 30)));
              setEditing(false);
            }
          }}
          inputMode="decimal"
        />
      ) : (
        <span
          className={`${styles.metaValue} ${disabled ? '' : styles.metaValueEditable}`}
          onClick={disabled ? undefined : () => setEditing(true)}
          role={disabled ? undefined : 'button'}
          tabIndex={disabled ? undefined : 0}
          onKeyDown={disabled ? undefined : (e) => e.key === 'Enter' && setEditing(true)}
        >
          ${Math.round(dailyBudget * 30).toLocaleString()}/mo
        </span>
      )}
      <span className={styles.metaLabel}>Monthly budget{helperText ? ` · ${helperText}` : ''}</span>
    </div>
  );
}

export function CollapsibleSection({
  title,
  meta,
  defaultOpen = false,
  onRenameTitle,
  children,
}: {
  title: string;
  meta?: React.ReactNode;
  defaultOpen?: boolean;
  /** When provided, the title becomes an inline-editable input. */
  onRenameTitle?: (v: string) => void;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [draft, setDraft] = useState(title);
  const editingRef = useRef(false);

  useEffect(() => {
    if (!editingRef.current) setDraft(title);
  }, [title]);

  return (
    <div className={`${styles.collapsible} ${open ? styles.collapsibleOpen : ''}`}>
      <button className={styles.collapsibleHeader} onClick={() => setOpen(!open)} type="button">
        <div className={styles.collapsibleLeft}>
          <span
            className={`${styles.collapsibleChevron} ${open ? styles.collapsibleChevronOpen : ''}`}
          >
            <ChevronRight size={14} aria-hidden />
          </span>
          {onRenameTitle ? (
            <input
              className={styles.titleInput}
              value={draft}
              onClick={(e) => e.stopPropagation()}
              onFocus={() => {
                editingRef.current = true;
              }}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => {
                editingRef.current = false;
                const trimmed = draft.trim() || title;
                setDraft(trimmed);
                if (trimmed !== title) onRenameTitle(trimmed);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') {
                  setDraft(title);
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
          ) : (
            <h3 className={styles.sectionTitle}>{title}</h3>
          )}
        </div>
        {meta && <span className={styles.sectionMeta}>{meta}</span>}
      </button>
      {open && <div className={styles.collapsibleBody}>{children}</div>}
    </div>
  );
}
